import React from 'react';
import { Alert, Button, Col, Popconfirm, Row, Space, Typography } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { L } from '@lib';
import { Utils } from '@utils';
import { SuccessButton } from '@components';

const { Text } = Typography;

interface IProps {
  isRefund: boolean;
  amount?: number;
  startedAt?: string;
  lastTerminalMessage?: string;
  resolving: boolean;
  onConfirmPaid: () => void;
  onConfirmNotPaid: () => void;
}

/**
 * Dojo docs (Terminals → Expired state): when the card machine result cannot be confirmed, explain the error, prompt
 * the operator to check the card machine screen or receipt, and let them record the payment as approved or retry.
 */
export const PaymentConfirmationPanel: React.FC<IProps> = React.memo(
  ({ isRefund, amount, startedAt, lastTerminalMessage, resolving, onConfirmPaid, onConfirmNotPaid }) => {
    const subject = isRefund ? 'refund' : 'payment';

    return (
      <Alert
        type="warning"
        showIcon
        message={L('Card machine result not confirmed')}
        description={
          <Space vertical size={12} style={{ width: '100%' }}>
            <Text>
              {L('Communication with the card machine has failed. Please check the card machine for the result.')}
            </Text>
            <Text type="secondary">
              {amount !== undefined && `${Utils.formatCurrency(amount)} `}
              {startedAt && `· ${L('started')} ${new Date(startedAt).toLocaleTimeString()} `}
              {lastTerminalMessage && `· ${L('last card machine message')}: ${lastTerminalMessage}`}
            </Text>
            <Text strong>{L(`Was the ${subject} approved on the card machine?`)}</Text>
            <Row gutter={[0, 8]}>
              <Col span={24}>
                <Popconfirm
                  title={L(`Record the ${subject} as approved?`)}
                  description={L(
                    'Only confirm if the card machine shows APPROVED or printed a customer receipt.'
                  )}
                  icon={<ExclamationCircleFilled />}
                  okText={L('Yes, it was approved')}
                  cancelText={L('Cancel')}
                  onConfirm={onConfirmPaid}
                  disabled={resolving}
                >
                  <SuccessButton block loading={resolving} disabled={resolving}>
                    {L('Yes – approved on card machine')}
                  </SuccessButton>
                </Popconfirm>
              </Col>
              <Col span={24}>
                <Button danger block onClick={onConfirmNotPaid} disabled={resolving}>
                  {L(`No – ${subject} failed`)}
                </Button>
              </Col>
            </Row>
          </Space>
        }
      />
    );
  }
);
